import React, { useCallback, useContext, useEffect, useLayoutEffect, useRef, useState } from 'react';
import cn from 'classnames';
import { useDispatch } from 'react-redux';
import { UNSAFE_NavigationContext, useNavigate } from 'react-router-dom';
import Icon from 'src/components/common/Icon';
import StorycardsCredits from 'src/components/common/StorycardsCredits';
import { RESULTS_ROUTE, ROOT } from 'src/constants/routes';
import { useAppSelector } from 'src/hooks/store';
import { AppDispatch } from 'src/store';
import { markVersionMismatch } from 'src/store/data';
import { getDuels, getStatements } from 'src/store/data/actions';
import {
  selectDuels,
  selectLoadingDuels,
  selectLoadingStatements,
  selectParties,
  selectStatements,
  selectVersion,
  selectVersionMismatch,
} from 'src/store/data/selectors';
import { Party, Statement } from 'src/types/data-types';
import { analytics } from 'src/utils/analytics';
import { clientSession } from 'src/utils/session-storage';

import ComparisonSection from './ComparisonSection';
import css from './Questions.module.scss';
import { getSessionDuel, holdQuizHistory } from './helpers';

type Answer = '100' | '0';

const SWIPE_THRESHOLD = 108;
const FLICK_VELOCITY = 0.62;
const FLICK_MIN_DISTANCE = 42;
const EXIT_DISTANCE = 520;
// Distance the front card travels while the card behind eases into its place.
const LIFT_DISTANCE = 240;
const ROTATION_FACTOR = 0.065;
const MAX_ROTATION = 14;
const SNAP_EASE = 'cubic-bezier(0.22, 1, 0.36, 1)';
const EXIT_EASE = 'cubic-bezier(0.2, 0.8, 0.2, 1)';
const MOBILE_EXIT_EASE = 'cubic-bezier(0.4, 0.15, 0.2, 1)';
const MOBILE_SWIPE_QUERY = '(max-width: 767px), (max-width: 1024px) and (orientation: portrait)';
const noBtnLabel = 'אני נגד';
const yesBtnLabel = 'אני בעד';

function isMobileSwipe() {
  return window.matchMedia(MOBILE_SWIPE_QUERY).matches;
}

function dragRotation(x: number) {
  const raw = x * ROTATION_FACTOR;
  const limited = MAX_ROTATION * Math.tanh(Math.abs(raw) / MAX_ROTATION);

  return Math.sign(raw) * limited;
}

function deckLift(x: number) {
  return Math.max(0, Math.min(1, Math.abs(x) / LIFT_DISTANCE));
}

function motionFor(kind: 'exit' | 'snap', traveled = 0, velocity = 0) {
  const mobile = isMobileSwipe();

  if (kind === 'snap') {
    return { ms: mobile ? 420 : 220, ease: SNAP_EASE };
  }

  if (mobile) {
    const base = 780;
    const min = 680;
    const progress = Math.min(1, Math.abs(traveled) / EXIT_DISTANCE);
    const ms = Math.round(base - progress * (base - min));

    return { ms, ease: MOBILE_EXIT_EASE };
  }

  const base = 340;
  const min = 220;

  if (Math.abs(velocity) < 0.05 && Math.abs(traveled) < 8) {
    return { ms: base, ease: EXIT_EASE };
  }

  const remaining = Math.max(80, EXIT_DISTANCE - Math.abs(traveled));
  const speed = Math.max(Math.abs(velocity), 1.55);
  const ms = Math.round(Math.min(base, Math.max(min, remaining / speed)));

  return { ms, ease: EXIT_EASE };
}

export default function Questions() {
  const navigate = useNavigate();
  const { navigator } = useContext(UNSAFE_NavigationContext);
  const dispatch = useDispatch<AppDispatch>();
  const [sessionReady, setSessionReady] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [swipeIntent, setSwipeIntent] = useState<Answer | null>(null);
  const [dragging, setDragging] = useState(false);
  const [leaving, setLeaving] = useState(false);
  const duels = useAppSelector(selectDuels);
  const statements = useAppSelector(selectStatements);
  const loadingDuels = useAppSelector(selectLoadingDuels);
  const duel = getSessionDuel(duels);
  const didRequestDuels = useRef(false);
  const loadingStatements = useAppSelector(selectLoadingStatements);
  const didRequestStatements = useRef(false);
  const parties = useAppSelector(selectParties);
  const liveVersionId = useAppSelector(selectVersion);
  const versionMismatch = useAppSelector(selectVersionMismatch);

  const frontCardRef = useRef<HTMLDivElement>(null);
  const deckRef = useRef<HTMLDivElement>(null);
  const dragRef = useRef({
    active: false,
    pointerId: 0,
    startX: 0,
    startY: 0,
    x: 0,
    y: 0,
    lastX: 0,
    lastTime: 0,
    velocityX: 0,
  });
  const frameRef = useRef<number | null>(null);
  const motionFrameRef = useRef<number | null>(null);
  const pendingAnswerRef = useRef<Answer | null>(null);
  const leaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const duelRef = useRef(duel);
  const currentIndexRef = useRef(currentIndex);
  const statementsRef = useRef(statements);
  const partiesRef = useRef(parties);
  const restoreProgressRef = useRef<() => void>(() => undefined);

  duelRef.current = duel;
  currentIndexRef.current = currentIndex;
  statementsRef.current = statements;
  partiesRef.current = parties;

  const isComplete = Boolean(duel) && currentIndex >= (duel?.length ?? 0);
  // One extra card stays hidden behind the stack and slides into the back slot as the front card leaves.
  const visibleCards = duel ? duel.slice(currentIndex, currentIndex + 4) : [];

  useEffect(() => {
    const root = document.documentElement;

    const update = () => {
      const viewport = window.visualViewport?.height ?? window.innerHeight;
      const header = document.querySelector('header');
      const headerHeight = header?.getBoundingClientRect().height ?? 60;

      root.style.setProperty('--questions-viewport', `${Math.round(viewport)}px`);
      root.style.setProperty('--questions-header', `${Math.round(headerHeight)}px`);
    };

    update();

    const viewport = window.visualViewport;
    viewport?.addEventListener('resize', update);
    viewport?.addEventListener('scroll', update);
    window.addEventListener('orientationchange', update);

    return () => {
      viewport?.removeEventListener('resize', update);
      viewport?.removeEventListener('scroll', update);
      window.removeEventListener('orientationchange', update);
      root.style.removeProperty('--questions-viewport');
      root.style.removeProperty('--questions-header');
    };
  }, []);

  useEffect(() => {
    // useEffect, not useLayoutEffect: HistoryRouter subscribes in its own layout
    // effect, which runs after this component. A layout redirect updates the URL
    // before that subscription exists, so the questions view stays mounted.
    const session = clientSession.get();

    if (!clientSession.isValid(session)) {
      navigate(ROOT, { replace: true });
      return;
    }

    setCurrentIndex(session.currentQuestionIndex);
    setSessionReady(true);
  }, [navigate]);

  useEffect(() => {
    if (!versionMismatch) {
      return;
    }

    clientSession.remove();
    navigate(ROOT, { replace: true });
  }, [navigate, versionMismatch]);

  useEffect(() => {
    if (!sessionReady || !liveVersionId) {
      return;
    }

    const session = clientSession.get();

    if (!session?.version || session.version === liveVersionId) {
      return;
    }

    // getParties was refreshed with a new active version. Drop the quiz instead of mixing it.
    clientSession.remove();
    dispatch(markVersionMismatch());
  }, [dispatch, liveVersionId, sessionReady]);

  useEffect(() => {
    if (!sessionReady || !Array.isArray(parties) || parties.length === 0) {
      return;
    }

    const session = clientSession.get();

    if (!clientSession.isValid(session)) {
      navigate(ROOT, { replace: true });
      return;
    }

    const knownPartyIds = new Set(parties.map((party: Party) => party.id));

    if (!knownPartyIds.has(session.partyAId) || !knownPartyIds.has(session.partyBId)) {
      navigate(ROOT, { replace: true });
    }
  }, [navigate, parties, sessionReady]);

  useEffect(() => {
    if (!sessionReady) {
      return undefined;
    }

    return holdQuizHistory(navigator, () => {
      restoreProgressRef.current();
    });
  }, [navigator, sessionReady]);

  useEffect(() => {
    if (!sessionReady || versionMismatch || duels || loadingDuels || didRequestDuels.current) {
      return;
    }

    didRequestDuels.current = true;
    void dispatch(getDuels());
  }, [duels, loadingDuels, dispatch, sessionReady, versionMismatch]);

  useEffect(() => {
    if (!sessionReady || versionMismatch || statements || loadingStatements || didRequestStatements.current) {
      return;
    }

    didRequestStatements.current = true;
    void dispatch(getStatements());
  }, [statements, loadingStatements, dispatch, sessionReady, versionMismatch]);

  useEffect(() => {
    if (!sessionReady || !isComplete) {
      return;
    }

    if (!clientSession.hasValidAnswers()) {
      navigate(ROOT, { replace: true });
      return;
    }

    navigate(RESULTS_ROUTE, { replace: true });
  }, [isComplete, navigate, sessionReady]);

  const applyCardTransform = useCallback((x: number, y: number) => {
    const card = frontCardRef.current;

    if (!card) {
      return;
    }

    const swipe = Math.max(-1, Math.min(1, x / SWIPE_THRESHOLD));
    const deck = deckRef.current;

    if (deck) {
      deck.classList.add(css.deckFollow);
      deck.style.setProperty('--lift', String(deckLift(x)));
    }

    card.style.transition = 'none';
    card.style.transform = `translate3d(${x}px, ${y}px, 0) rotate(${dragRotation(x)}deg)`;
    card.style.setProperty('--swipe', String(swipe));
  }, []);

  const animateCardTo = useCallback(
    (
      fromX: number,
      fromY: number,
      toX: number,
      toY: number,
      motion: { ms: number; ease: string },
      onStart?: () => void
    ) => {
      const card = frontCardRef.current;

      if (!card) {
        return;
      }

      if (motionFrameRef.current != null) {
        cancelAnimationFrame(motionFrameRef.current);
        motionFrameRef.current = null;
      }

      const swipe = Math.max(-1, Math.min(1, toX / SWIPE_THRESHOLD));
      const deck = deckRef.current;
      const liftTarget = toX === 0 ? 0 : 1;

      if (deck) {
        // Duration is set while transitions are off, so the next change eases
        // instead of snapping to the new lift.
        deck.classList.add(css.deckFollow);
        deck.style.setProperty('--deck-ms', `${motion.ms}ms`);
        deck.style.setProperty('--deck-ease', motion.ease);
        deck.style.setProperty('--lift', String(deckLift(fromX)));
      }

      card.style.transition = 'none';
      card.style.transform = `translate3d(${fromX}px, ${fromY}px, 0) rotate(${dragRotation(fromX)}deg)`;

      const applyTarget = () => {
        motionFrameRef.current = null;

        if (frontCardRef.current !== card) {
          return;
        }

        card.style.transition = `transform ${motion.ms}ms ${motion.ease}`;
        card.style.transform = `translate3d(${toX}px, ${toY}px, 0) rotate(${dragRotation(toX)}deg)`;
        card.style.setProperty('--swipe', String(swipe));

        if (deck) {
          deck.classList.remove(css.deckFollow);
          deck.style.setProperty('--lift', String(liftTarget));
        }

        onStart?.();
      };

      // iOS drops a transition that starts in the same turn as touchend.
      if (isMobileSwipe()) {
        motionFrameRef.current = requestAnimationFrame(() => {
          motionFrameRef.current = requestAnimationFrame(applyTarget);
        });
        return;
      }

      void card.offsetWidth;
      applyTarget();
    },
    []
  );

  const resetCardTransform = useCallback(() => {
    const deck = deckRef.current;

    if (deck) {
      deck.classList.remove(css.deckFollow);
      deck.style.setProperty('--deck-ms', '0ms');
      deck.style.setProperty('--lift', '0');
    }

    const card = frontCardRef.current;

    if (!card) {
      return;
    }

    // Drop inline motion and let the stack transform take over. Writing a new
    // transform here snaps the incoming card off its eased pose.
    card.style.transition = '';
    card.style.transform = '';
    card.style.removeProperty('--swipe');
  }, []);

  const commitAnswer = useCallback(() => {
    const answer = pendingAnswerRef.current;
    const statementId = duelRef.current?.[currentIndexRef.current];

    if (!answer) {
      return;
    }

    pendingAnswerRef.current = null;
    if (leaveTimerRef.current) {
      clearTimeout(leaveTimerRef.current);
      leaveTimerRef.current = null;
    }
    dragRef.current.active = false;
    dragRef.current.x = 0;
    dragRef.current.y = 0;

    const nextIndex = currentIndexRef.current + 1;

    if (statementId) {
      const session = clientSession.get();
      const questionNumber = currentIndexRef.current + 1;
      const text = statementsRef.current?.find((st: Statement) => st.id === statementId)?.text ?? '';
      const partyA = partiesRef.current?.find((p: Party) => p.id === session?.partyAId);
      const partyB = partiesRef.current?.find((p: Party) => p.id === session?.partyBId);

      analytics.gtag.event(`שאלה מספר ${questionNumber}`, {
        Dynamic_Question: text,
        Dynamic_Answer: answer === '100' ? yesBtnLabel : noBtnLabel,
        Party_Name_1: partyA?.name ?? '',
        Party_Name_2: partyB?.name ?? '',
      });

      try {
        clientSession.saveAnswer(statementId, answer === '100' ? 100 : 0, nextIndex);
      } catch {
        navigate(ROOT, { replace: true });
        return;
      }
    } else {
      clientSession.setCurrentQuestionIndex(nextIndex);
    }

    setCurrentIndex(nextIndex);
    setLeaving(false);
    setSwipeIntent(null);
  }, [navigate]);

  const finishSwipe = useCallback(
    (answer: Answer, fromX = 0, fromY = 0, velocity = 0) => {
      if (pendingAnswerRef.current) {
        return;
      }

      pendingAnswerRef.current = answer;
      setDragging(false);
      setLeaving(true);
      setSwipeIntent(answer);

      const direction = answer === '100' ? 1 : -1;
      const motion = motionFor('exit', fromX, velocity);

      animateCardTo(fromX, fromY, direction * EXIT_DISTANCE, 16, motion, () => {
        // Start when the fly-off starts (after the iOS frame delay), and a little
        // after it ends, so the deck is not swapped on the last in-between frame.
        leaveTimerRef.current = setTimeout(commitAnswer, motion.ms + 32);
      });
    },
    [animateCardTo, commitAnswer]
  );

  useLayoutEffect(() => {
    resetCardTransform();
  }, [currentIndex, resetCardTransform]);

  useEffect(() => {
    return () => {
      if (leaveTimerRef.current) {
        clearTimeout(leaveTimerRef.current);
      }

      if (frameRef.current != null) {
        cancelAnimationFrame(frameRef.current);
      }

      if (motionFrameRef.current != null) {
        cancelAnimationFrame(motionFrameRef.current);
      }
    };
  }, []);

  const scheduleDragPaint = useCallback(() => {
    if (frameRef.current != null) {
      return;
    }

    frameRef.current = requestAnimationFrame(() => {
      frameRef.current = null;

      if (!dragRef.current.active) {
        return;
      }

      applyCardTransform(dragRef.current.x, dragRef.current.y);
    });
  }, [applyCardTransform]);

  const onFrontCardTransitionEnd = (event: React.TransitionEvent<HTMLDivElement>) => {
    if (event.propertyName !== 'transform' || !pendingAnswerRef.current) {
      return;
    }

    commitAnswer();
  };

  const onPointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    if (leaving || isComplete) {
      return;
    }

    dragRef.current = {
      active: true,
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      x: 0,
      y: 0,
      lastX: 0,
      lastTime: performance.now(),
      velocityX: 0,
    };

    if (event.cancelable) {
      event.preventDefault();
    }

    event.currentTarget.setPointerCapture(event.pointerId);
    setDragging(true);
    applyCardTransform(0, 0);
  };

  const onPointerMove = (event: React.PointerEvent<HTMLDivElement>) => {
    if (!dragRef.current.active || dragRef.current.pointerId !== event.pointerId || leaving) {
      return;
    }

    if (event.cancelable) {
      event.preventDefault();
    }

    const x = event.clientX - dragRef.current.startX;
    const y = Math.max(-32, Math.min(32, (event.clientY - dragRef.current.startY) * 0.12));
    const now = performance.now();
    const dt = now - dragRef.current.lastTime;

    if (dt > 0 && dt < 64) {
      const instant = (x - dragRef.current.lastX) / dt;

      dragRef.current.velocityX = dragRef.current.velocityX * 0.72 + instant * 0.28;
    }

    dragRef.current.x = x;
    dragRef.current.y = y;
    dragRef.current.lastX = x;
    dragRef.current.lastTime = now;
    scheduleDragPaint();

    const nextIntent = Math.abs(x) < 28 ? null : x > 0 ? '100' : '0';

    setSwipeIntent(prev => (prev === nextIntent ? prev : nextIntent));
  };

  const onPointerUp = (event: React.PointerEvent<HTMLDivElement>) => {
    if (!dragRef.current.active || dragRef.current.pointerId !== event.pointerId || leaving) {
      return;
    }

    dragRef.current.active = false;
    setDragging(false);

    if (frameRef.current != null) {
      cancelAnimationFrame(frameRef.current);
      frameRef.current = null;
    }

    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }

    const { x, y, velocityX } = dragRef.current;
    const flicked =
      Math.abs(velocityX) >= FLICK_VELOCITY &&
      Math.abs(x) >= FLICK_MIN_DISTANCE &&
      Math.sign(velocityX) === Math.sign(x);

    if (Math.abs(x) >= SWIPE_THRESHOLD || flicked) {
      finishSwipe(x >= 0 ? '100' : '0', x, y, velocityX);
      return;
    }

    animateCardTo(x, y, 0, 0, motionFor('snap'));
    dragRef.current.x = 0;
    dragRef.current.y = 0;
    dragRef.current.velocityX = 0;
    setSwipeIntent(null);
  };

  const onAnswerClick = (answer: Answer) => {
    if (leaving || isComplete || dragging) {
      return;
    }

    finishSwipe(answer);
  };

  const renderStatementText = (cardId: string) => {
    if (!statements) return;
    const text = statements.find((st: Statement) => st.id === cardId)?.text ?? '';
    return <p className={css.cardQuestion}>{text}</p>;
  };

  restoreProgressRef.current = () => {
    const session = clientSession.get();

    if (!clientSession.isValid(session)) {
      navigate(ROOT, { replace: true });
      return;
    }

    if (leaveTimerRef.current) {
      clearTimeout(leaveTimerRef.current);
      leaveTimerRef.current = null;
    }

    pendingAnswerRef.current = null;
    dragRef.current.active = false;
    dragRef.current.x = 0;
    dragRef.current.y = 0;
    dragRef.current.velocityX = 0;
    setCurrentIndex(session.currentQuestionIndex);
    setDragging(false);
    setLeaving(false);
    setSwipeIntent(null);
    resetCardTransform();
  };

  const renderCardHeader = (cardId: string, questionNumber: number) => {
    if (!statements) return;
    const topicName = statements.find((st: Statement) => st.id === cardId).topic.name ?? '';

    return (
      <div className={css.cardHeader}>
        <span className={css.cardIndex}>{`#${questionNumber}`}</span>
        <h3 className={css.cardTitle}>{topicName}</h3>
      </div>
    );
  };

  if (!sessionReady || versionMismatch) {
    return null;
  }

  return (
    <div className={css.questions}>
      <div className={css.questionsContent}>
        <ComparisonSection />

        <div className={css.answersProgress}>
          {duel &&
            visibleCards &&
            duel.map((_, index) => (
              <div
                className={cn(css.qwNumber, {
                  [css.active]: index === currentIndex && !isComplete,
                  [css.done]: index < currentIndex,
                })}
                key={index}
              >
                {index + 1}
              </div>
            ))}
        </div>

        <div className={css.cardsSlider}>
          <div className={css.deck} ref={deckRef}>
            {duel &&
              statements &&
              visibleCards
                .map((cardId: string, stackIndex: number) => {
                  const isFront = stackIndex === 0;
                  const questionIndex = currentIndex + stackIndex;
                  const questionNumber = questionIndex + 1;

                  return (
                    <div
                      className={cn(
                        css.slide,
                        isFront && css.front,
                        dragging && isFront && css.dragging,
                        leaving && isFront && css.leaving
                      )}
                      key={`${questionIndex}-${cardId}`}
                      ref={isFront ? frontCardRef : undefined}
                      style={{ '--stack': stackIndex } as React.CSSProperties}
                      onPointerDown={isFront ? onPointerDown : undefined}
                      onPointerMove={isFront ? onPointerMove : undefined}
                      onPointerUp={isFront ? onPointerUp : undefined}
                      onPointerCancel={isFront ? onPointerUp : undefined}
                      onTransitionEnd={isFront ? onFrontCardTransitionEnd : undefined}
                    >
                      {renderCardHeader(cardId, questionNumber)}
                      <Icon type="border" />
                      {renderStatementText(cardId)}
                    </div>
                  );
                })
                .reverse()}
          </div>

          <div className={css.actions}>
            <button
              type="button"
              className={cn(css.answerBtn, css.noBtn, swipeIntent === '0' && css.hot)}
              onClick={() => onAnswerClick('0')}
              disabled={leaving}
            >
              {noBtnLabel}
            </button>
            <button
              type="button"
              className={cn(css.answerBtn, css.yesBtn, swipeIntent === '100' && css.hot)}
              onClick={() => onAnswerClick('100')}
              disabled={leaving}
            >
              {yesBtnLabel}
            </button>
          </div>

          <StorycardsCredits className={css.storycardsCredits} />
        </div>
      </div>
    </div>
  );
}
